/**
 * Shared number-priority style maps for the trade-detail figure primitives
 * (Figure / SignedValue). A figure's SIZE encodes its rank — three tiers only,
 * per design-tokens `typeScale`. Kept in one module so Figure and SignedValue
 * can't drift on the tier/tone scale.
 *
 *   hero       34px / 500  — the 2 KPI heroes only (Net P&L, Rate)
 *   secondary  21px / 600  — the 3 secondary KPIs
 *   section    22px / 600  — waterfall steps, section tickers, P&L net
 *   body       14px        — inline figures, table rows
 *
 * Weights are deliberately medium, not bold — heavy weights read as "stamped"
 * and were rejected (DESIGN-SPEC §2).
 *
 * LR-436 D4: the design tokens specified 460/560 for hero/secondary, but the
 * static Inter @import (index.css) only loads 300/400/500/600/700, so the browser
 * snapped 460→500 and 560→600 — i.e. those arbitrary weights never actually
 * rendered. Pinned to the loaded weights so the code states what ships (no visual
 * change). To render the TRUE 460/560 the Inter @import must load the variable
 * axis (wght@300..700) — flagged for designer confirmation before that change.
 */
export type FigureTier = 'hero' | 'secondary' | 'section' | 'body' | 'inherit';
export type FigureTone = 'default' | 'pos' | 'neg' | 'accent' | 'muted' | 'warn';

export const FIGURE_TIER_CLASS: Record<FigureTier, string> = {
  hero: 'text-[34px] font-[500] tracking-[-0.4px] leading-none',
  secondary: 'text-[21px] font-[600] leading-none',
  section: 'text-[22px] font-[600] tracking-[-0.3px] leading-none',
  body: 'text-[14px]',
  // LR-553: inherit the container's size instead of pinning one. A dense ledger
  // sets its own row size (13px on the billing statement) and a figure inside a
  // cell must follow it - passing a size via `className` alongside a tier that
  // also sets one leaves two arbitrary text-[..] utilities of equal specificity,
  // where the winner depends on their order in the built stylesheet, not on the
  // class attribute. `.lr-dec` stays relative (0.58em), so the demoted tail
  // scales with whatever the container picked.
  inherit: '',
};

export const FIGURE_TONE_CLASS: Record<FigureTone, string> = {
  default: 'text-lr-fg',
  pos: 'text-lr-pos',
  neg: 'text-lr-neg',
  accent: 'text-lr-accent',
  muted: 'text-lr-muted',
  warn: 'text-lr-warn',
};
